import { PageController } from '@client/components/_core/layout/PageController';
import { type TabItem, Tabs } from '@client/components/_core/navigation/Tabs';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useCustomDocumentTitle } from '@client/hooks/ui/useCustomDocumentTitle';
import { trpc } from '@client/trpc/client';
import { isRetryableQueryError } from '@client/trpc/error-classification';
import type { ShareableRouteContext } from '@client/types/router';
import { createFileRoute, useChildMatches } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { Building2, Car, KeyRound, LayoutGrid, type LucideIcon, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ClientRecordHeader, isConsolidatorRecord } from './ClientRecordHeader';
import { ClientRecordActionsContext, useCanManageFleetClients, useClientRecord } from './client-record-data';
import { EditClientDrawer } from './EditClientDrawer';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id')({
    staticData: {
        presentation: 'fullPage',
        modules: ['fleet'],
        metadata: {
            title: 'Client details',
        },
        ui: {
            widthVariant: 'default',
            hideTitle: true,
        },
    },
    params: {
        parse({ id }) {
            return { id: BigInt(id) };
        },
    },
    beforeLoad(ctx): ShareableRouteContext {
        async function breadcrumb() {
            const client = await ctx.context.trpcUtils.fleetClientDb.getFleetClient.ensureData({ id: ctx.params.id });
            return client.name;
        }

        return {
            breadcrumb: { title: breadcrumb },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientRecord />
        </PageController>
    );
}

const LIST_ROUTE = '/console/$orgId/configuration/fleet/client-database';

function TabLabel({ icon: Icon, count, children }: { icon: LucideIcon; count?: number; children: string }) {
    return (
        <span className="flex items-center gap-2">
            <Icon className="size-3.5" />
            {children}
            {count !== undefined ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 font-semibold text-2xs text-muted-foreground tabular-nums">
                    {count}
                </span>
            ) : null}
        </span>
    );
}

function ClientRecord() {
    const orgId = useOrgId();
    const { id } = Route.useParams();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const isOrgAdmin = useCanManageFleetClients();
    const [isEditOpen, setIsEditOpen] = useState(false);

    const goToList = (options?: { replace?: boolean }) => navigate({ to: LIST_ROUTE, params: { orgId }, ...options });

    const clientQuery = useClientRecord(id);
    const client = clientQuery.data;
    const childMatches = useChildMatches();
    const isUnavailable = !!clientQuery.error && !isRetryableQueryError(clientQuery.error);

    // A missing or foreign-org id lands on the list with the server's message
    // rather than an empty record shell.
    useEffect(() => {
        if (isUnavailable) {
            errorMessage.show(clientQuery.error);
            goToList({ replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate only when the error changes
    }, [clientQuery.error, isUnavailable]);
    // Registered child routes can be opened directly even when their tab is hidden.
    useEffect(() => {
        if (!client || isUnavailable) return;
        const unsupportedTabs = isConsolidatorRecord(client)
            ? ['/vehicles', '/drivers', '/portal-access']
            : ['/clients'];
        if (childMatches.some(({ routeId }) => unsupportedTabs.some((tab) => routeId.endsWith(tab)))) {
            void navigate({
                to: '/console/$orgId/configuration/fleet/client-database/details/$id/overview',
                params: { orgId, id },
                replace: true,
            });
        }
    }, [client, childMatches, navigate, orgId, id, isUnavailable]);

    const queryError = clientQuery.error ? (
        <InlineQueryError
            compact={!!client}
            title="Could not load client record"
            description={
                isUnavailable
                    ? undefined
                    : client
                      ? 'The latest details could not be loaded. Previously loaded details and any unsaved edits have been kept.'
                      : 'The client record could not be loaded. Please try again.'
            }
            error={clientQuery.error}
            onRetry={() => void clientQuery.refetch()}
            retrying={clientQuery.isFetching}
        />
    ) : null;
    const { mutate: deleteClient } = trpc.fleetClientDb.deleteFleetClient.useMutation();

    useCustomDocumentTitle(client?.name);

    if (!client || isUnavailable) {
        if (queryError) return queryError;
        return (
            <div className="rounded-lg border border-separator bg-card p-6">
                <Skeleton active />
            </div>
        );
    }

    const isConsolidator = isConsolidatorRecord(client);
    const overviewTab = {
        to: '/console/$orgId/configuration/fleet/client-database/details/$id/overview',
        label: <TabLabel icon={LayoutGrid}>Overview</TabLabel>,
    } as const satisfies TabItem;
    const tabItems: TabItem[] = isConsolidator
        ? [
              overviewTab,
              {
                  to: '/console/$orgId/configuration/fleet/client-database/details/$id/clients',
                  label: (
                      <TabLabel icon={Building2} count={client.childrenCount}>
                          Clients
                      </TabLabel>
                  ),
              },
          ]
        : [
              overviewTab,
              {
                  to: '/console/$orgId/configuration/fleet/client-database/details/$id/vehicles',
                  label: (
                      <TabLabel icon={Car} count={client.vehiclesCount}>
                          Vehicles
                      </TabLabel>
                  ),
              },
              {
                  to: '/console/$orgId/configuration/fleet/client-database/details/$id/drivers',
                  label: (
                      <TabLabel icon={Users} count={client.driversCount}>
                          Drivers
                      </TabLabel>
                  ),
              },
              {
                  to: '/console/$orgId/configuration/fleet/client-database/details/$id/portal-access',
                  label: <TabLabel icon={KeyRound}>Portal access</TabLabel>,
              },
          ];

    const onDelete = () => {
        const noun = isConsolidator ? 'consolidator' : 'client';
        confirm({
            title: `Delete ${client.name}?`,
            content: isConsolidator
                ? 'A consolidator can only be deleted once it has no active clients. Move or delete those clients first.'
                : 'The client is removed from the client database. A client can only be deleted once it has no vehicles, drivers, cost centres, depots or portal users.',
            okText: `Delete ${noun}`,
            okDanger: true,
            onOk() {
                deleteClient(
                    { id: client.id },
                    {
                        onSuccess() {
                            message.success(`${isConsolidator ? 'Consolidator' : 'Client'} deleted.`);
                            // Invalidate the list only: refetching the deleted record here
                            // could land a NOT_FOUND before unmount and show a second
                            // error on top of the success toast.
                            void trpcUtils.fleetClientDb.listFleetClients.invalidate();
                            goToList();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    return (
        <div className="flex flex-col gap-4">
            {queryError}
            <ClientRecordHeader
                client={client}
                orgId={orgId}
                canEdit={isOrgAdmin}
                onEdit={() => setIsEditOpen(true)}
                onDelete={onDelete}
                backLink={
                    <BackLink to={LIST_ROUTE} params={{ orgId }}>
                        Back to client database
                    </BackLink>
                }
            />

            <ClientRecordActionsContext value={{ openEdit: () => setIsEditOpen(true) }}>
                <Tabs items={tabItems} />
            </ClientRecordActionsContext>

            <EditClientDrawer
                open={isEditOpen}
                client={client}
                onClose={() => setIsEditOpen(false)}
                onSaved={() => {
                    setIsEditOpen(false);
                    void trpcUtils.fleetClientDb.listFleetClients.invalidate();
                    void clientQuery.refetch();
                }}
            />
        </div>
    );
}
